import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Layout as AntLayout, Menu, Avatar, Dropdown, Space, Badge } from 'antd';
import { APP_TITLE } from '../../config/appBranding';
import {
  DashboardOutlined,
  AppstoreOutlined,
  AuditOutlined,
  SettingOutlined,
  UserOutlined,
  TeamOutlined,
  FileTextOutlined,
  LogoutOutlined,
  LockOutlined,
  IdcardOutlined,
  ClockCircleOutlined,
  AccountBookOutlined,
  WalletOutlined,
  InboxOutlined,
  ShopOutlined,
  ApartmentOutlined,
  FormOutlined,
  BankOutlined,
  ProjectOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { routePermissionMap } from '../../config/routePermissions';
import { oaModuleMenus, getOpenMenuKeysByPath } from '../../config/moduleMenus';
import { hasPermission as checkPermission } from '../../utils/permission';
import { useApprovalNotify } from '../../hooks/useApprovalNotify';
import { useInbox } from '../../hooks/useInbox';
import { useDocumentTitleAlert } from '../../hooks/useDocumentTitleAlert';
import ApprovalNotifyBanner from '../ApprovalNotify/ApprovalNotifyBanner';
import InboxNotifyBanner from '../Inbox/InboxNotifyBanner';
import InboxBell from '../Inbox/InboxBell';
import { ApprovalNotifyProvider } from '../../contexts/ApprovalNotifyContext';
import ChangePasswordModal from './ChangePasswordModal';
import './Layout.css';

const { Header, Sider, Content } = AntLayout;

const ICON_MAP = {
  IdcardOutlined: <IdcardOutlined />,
  ClockCircleOutlined: <ClockCircleOutlined />,
  AccountBookOutlined: <AccountBookOutlined />,
  WalletOutlined: <WalletOutlined />,
  InboxOutlined: <InboxOutlined />,
  ShopOutlined: <ShopOutlined />,
  BankOutlined: <BankOutlined />,
  ProjectOutlined: <ProjectOutlined />,
  FileTextOutlined: <FileTextOutlined />
};

const ROOT_SUBMENU_KEYS = [...oaModuleMenus.map((module) => module.key), 'system'];

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [openKeys, setOpenKeys] = useState([]);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);

  const resolveOpenKeyByPath = useCallback((pathname) => {
    if (pathname.startsWith('/system/')) {
      return 'system';
    }
    const keys = getOpenMenuKeysByPath(pathname);
    return keys[0] || null;
  }, []);

  useEffect(() => {
    const activeKey = resolveOpenKeyByPath(location.pathname);
    setOpenKeys(activeKey ? [activeKey] : []);
  }, [location.pathname, resolveOpenKeyByPath]);

  const handleOpenChange = (keys) => {
    const latestOpenKey = keys.find((key) => !openKeys.includes(key));

    if (!latestOpenKey || !ROOT_SUBMENU_KEYS.includes(latestOpenKey)) {
      setOpenKeys(keys);
      return;
    }

    setOpenKeys([latestOpenKey]);
  };

  const handleCollapse = (value) => {
    setCollapsed(value);
    if (value) {
      setOpenKeys([]);
      return;
    }
    const activeKey = resolveOpenKeyByPath(location.pathname);
    setOpenKeys(activeKey ? [activeKey] : []);
  };

  const hasPermission = useCallback((permCode) => checkPermission(user, permCode), [user]);
  const canApprovalCenter = hasPermission('oa:approval:center');
  const {
    badge: approvalBadge,
    visiblePopups,
    dismissPopup,
    dismissAllPopups,
    refresh: refreshApproval
  } = useApprovalNotify(!!user && canApprovalCenter);

  const {
    unreadTotal: inboxUnread,
    recent: inboxRecent,
    visiblePopups: inboxVisiblePopups,
    dismissPopup: dismissInboxPopup,
    dismissAllPopups: dismissAllInboxPopups,
    markRead: markInboxRead,
    markAllRead: markAllInboxRead,
    refresh: refreshInbox
  } = useInbox(!!user);

  const inboxPopupTopOffset = useMemo(() => {
    if (!visiblePopups?.length) return 72;
    const bannerHeight = 132;
    const dismissAllHeight = visiblePopups.length > 1 ? 36 : 0;
    return 72 + visiblePopups.length * bannerHeight + dismissAllHeight + 12;
  }, [visiblePopups]);

  useDocumentTitleAlert(!!user && canApprovalCenter, approvalBadge);

  const filterMenuItems = useCallback((items) => {
    return items.map((item) => {
      if (item.children?.length) {
        const filteredChildren = filterMenuItems(item.children);
        if (!filteredChildren.length) return null;
        return { ...item, children: filteredChildren };
      }
      const permCode = routePermissionMap[item.key];
      if (permCode && !hasPermission(permCode)) return null;
      return item;
    }).filter(Boolean);
  }, [hasPermission]);

  const businessMenuItems = useMemo(() =>
    oaModuleMenus.map((module) => ({
      key: module.key,
      icon: ICON_MAP[module.icon],
      label: module.label,
      children: module.children.map((page) => ({
        key: page.path,
        label: page.label
      }))
    })),
  []);

  const approvalMenuLabel = useMemo(() => (
    <Badge count={approvalBadge.total} size="small" offset={[10, 0]} className="approval-center-menu-badge">
      <span className="approval-center-menu-label">审批中心</span>
    </Badge>
  ), [approvalBadge.total]);

  const allMenuItems = useMemo(() => ([
    { key: '/dashboard', icon: <DashboardOutlined />, label: '工作台' },
    { key: '/portal', icon: <AppstoreOutlined />, label: '应用门户' },
    { key: '/approval-center', icon: <AuditOutlined />, label: approvalMenuLabel },
    ...businessMenuItems,
    {
      key: 'system',
      icon: <SettingOutlined />,
      label: '系统管理',
      children: [
        { key: '/system/users', icon: <UserOutlined />, label: '用户管理' },
        { key: '/system/roles', icon: <TeamOutlined />, label: '角色管理' },
        { key: '/system/operation-logs', icon: <FileTextOutlined />, label: '操作日志' },
        { key: '/system/workflows', icon: <ApartmentOutlined />, label: '审批流管理' },
        { key: '/system/onboarding-form-config', icon: <FormOutlined />, label: '入职表单配置管理' },
        { key: '/system/ops-board', icon: <DashboardOutlined />, label: '运维看板' }
      ]
    }
  ]), [businessMenuItems, approvalMenuLabel]);

  const menuItems = useMemo(() => filterMenuItems(allMenuItems), [allMenuItems, filterMenuItems]);

  const userMenuItems = [
    {
      key: 'change-password',
      icon: <LockOutlined />,
      label: '修改密码',
      onClick: () => setPasswordModalOpen(true)
    },
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: () => {
        logout();
        navigate('/login');
      }
    }
  ];

  return (
    <AntLayout className="layout-container">
      <Sider collapsible collapsed={collapsed} onCollapse={handleCollapse} width={240}>
        <div className="logo">
          <h2>{collapsed ? 'OA' : APP_TITLE}</h2>
        </div>
        <div className="sider-menu-wrapper">
          <Menu
            theme="dark"
            mode="inline"
            className="oa-sider-menu"
            selectedKeys={[location.pathname]}
            openKeys={collapsed ? [] : openKeys}
            onOpenChange={handleOpenChange}
            items={menuItems}
            onClick={({ key }) => {
              if (key.startsWith('/')) navigate(key);
            }}
          />
        </div>
      </Sider>
      <AntLayout>
        <ApprovalNotifyBanner
          items={visiblePopups}
          onDismiss={dismissPopup}
          onDismissAll={dismissAllPopups}
        />
        <InboxNotifyBanner
          items={inboxVisiblePopups}
          topOffset={inboxPopupTopOffset}
          onDismiss={dismissInboxPopup}
          onDismissAll={dismissAllInboxPopups}
        />
        <Header className="layout-header">
          <div className="header-right">
            {user && (
              <InboxBell
                unreadTotal={inboxUnread}
                recent={inboxRecent}
                onMarkRead={markInboxRead}
                onMarkAllRead={markAllInboxRead}
              />
            )}
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Space style={{ cursor: 'pointer' }}>
                <Avatar icon={<UserOutlined />} src={user?.avatar} />
                <span>{user?.real_name || user?.username}</span>
              </Space>
            </Dropdown>
          </div>
        </Header>
        <Content className="layout-content">
          <ApprovalNotifyProvider value={{ refresh: refreshApproval, badge: approvalBadge, refreshInbox }}>
            {children}
          </ApprovalNotifyProvider>
        </Content>
      </AntLayout>
      <ChangePasswordModal
        open={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
      />
    </AntLayout>
  );
};

export default Layout;
